import * as React from 'react';
import {
  isObject,
  isArray,
  isString,
  isNumber,
  isFunction,
  uniq,
  isEmpty,
} from 'lodash';
import { globalElements } from '../glob';
import { genId } from '../common/util';
import { filterProperties } from '../common/filterProperties';
import { Item, ItemProps } from '../item';

export interface listProps {
  header?: string | number | React.ReactElement | ItemProps;
  footer?: string | number | React.ReactElement | ItemProps;
  listClass?: string;
  listStyle?: object;
  type?: object; // scroll swiper
  data: Array<ItemProps>;
  itemMethod?: object; // 批量设置每个子项的事件方法
  itemClass?: string;
  itemStyle?: object;
  attr?: object;

  [propName: string]: any; // react的事件体系, 其他属性  @item @list
}

function adapterChildProps(param: any) {
  let { index, subItem, config, listUniqId, currentData } = param;
  const { itemClass, itemStyle, itemMethod, methods } = config;
  if (index === -1 || index === undefined) {
    index = config.data.length - 1;
  }
  if (isString(subItem) || isNumber(subItem) || React.isValidElement(subItem)) {
    subItem = { title: subItem };
  }
  if (itemClass) {
    if (subItem.itemClass && subItem.itemClass.indexOf(itemClass) === -1) {
      subItem.itemClass = itemClass + ' ' + subItem.itemClass;
    } else {
      subItem.itemClass = itemClass;
    }
  }

  if (itemStyle) {
    if (subItem.itemStyle) {
      subItem.itemStyle = { ...itemStyle, ...subItem.itemStyle };
    } else {
      subItem.itemStyle = itemStyle;
    }
  }

  const uniqId =
    subItem.uniqId ||
    // (currentData && isEqual(currentData[index], subItem) && currentData[index].uniqId) ||
    listUniqId + '.' + genId(8);
  subItem = { ...subItem, ...(itemMethod || {}) };

  return {
    key: uniqId,
    uniqId,
    ...subItem,
    directChildType: 'list-child',
    directChildIndex: index,
  };
}

function adapterProps(props: any, uniqId: string) {
  const listConfig = {
    ...props,
    created: null,
    attached: null,
    ready: null,
    detached: null,
    moved: null,
    attr: null,
  };
  delete listConfig.created;
  delete listConfig.attached;
  delete listConfig.ready;
  delete listConfig.detached;
  delete listConfig.moved;
  delete listConfig.attr;
  const $data = listConfig.data.map((item: any, ii: number) =>
    adapterChildProps({
      subItem: item,
      index: ii,
      config: listConfig,
      listUniqId: uniqId,
      currentData: props.currentData,
    })
  );
  listConfig.data = $data;
  return listConfig;
}

function getDirectChilds(childs: any, uniqId: string) {
  const directChild: any = {};
  if (childs.length) {
    for (let id in childs) {
      if (id !== 'length') {
        if (id.length - uniqId.length === 9) {
          directChild[id] = childs[id];
        }
      }
    }
  }
  return directChild;
}

const ListNode = React.memo(Item, (prevprops, nextprops) => {
  if (Object.is(prevprops, nextprops)) {
    return true;
  }
  return false;
});

export const List: React.FC = React.forwardRef(
  (props: listProps, ref: any): any => {
    const [showState, setShowState] = React.useState(true);
    const id = props.id || props['$$id'];
    const uniqIdRef = React.useRef(props.uniqId || genId(8));
    const createdRef = React.useRef(false); // 生命周期 created
    const [uniqId] = React.useState<string>(uniqIdRef.current);
    const { events, notEvents, innerComponents, lifeTimes, attr } =
      filterProperties(props, uniqId);
    const propsRef = React.useRef(adapterProps(props, uniqId));
    const oriPropsRef = React.useRef({ ...propsRef.current });
    const domRef = ref || React.useRef(null);
    const [data, setData] = React.useState(propsRef.current.data);
    const [className, setClassName] = React.useState(
      propsRef.current.listClass || ''
    );
    const [style, setStyle] = React.useState(propsRef.current.listStyle || {});
    const [header, setHeader] = React.useState(propsRef.current.header || null);
    const [footer, setFooter] = React.useState(propsRef.current.footer || null);

    function actionCallback(cb) {
      if (isFunction(cb)) {
        cb();
      }
    }

    const context = {
      is: 'list',
      uniqId,
      reset(param?: listProps) {
        const newProps = { ...oriPropsRef.current, ...(param || {}) };
        let updateData = false;
        let updateListClass = false;
        let updateListStyle = false;
        let updateHeader = false;
        let updateFooter = false;
        if (param?.data) {
          updateData = true;
        }
        if (param?.listClass) {
          updateListClass = true;
        }
        if (param?.listStyle) {
          updateListStyle = true;
        }
        if (param?.header) {
          updateHeader = true;
        }
        if (param?.footer) {
          updateFooter = true;
        }
        updateListClass && setClassName(newProps.listClass);
        updateListStyle && setStyle(newProps.listClass);
        updateHeader && setHeader(newProps.header);
        updateFooter && setFooter(newProps.footer);
        updateData && setData(newProps.data);
      },
      getData() {
        const childs = {};
        const ids = Object.keys(globalElements);
        ids.forEach((id: string) => {
          if (id.indexOf(uniqId) === 0 && id.length - uniqId.length === 9) {
            childs[id] = globalElements[id].getData();
          }
        });
        return [...data].map((item: any) => childs[item.uniqId] || item);
      },
      children(cls?: string) {
        const ids = Object.keys(globalElements);
        let childType = cls;
        let childs: any = {};

        ids.forEach((id: string) => {
          if (id.indexOf(uniqId) === 0 && id.length > uniqId.length) {
            childs[id] = globalElements[id];
          }
        });

        if (cls) {
          if (cls.indexOf('>') === 0) {
            childs = getDirectChilds(childs, uniqId) || {};
            cls = cls.replace('>', '').trim();
          }
          if (cls) {
            childs = (() => {
              const tmp = {};
              for (let id in childs) {
                const child = childs[id];
                if (child.hasClass(cls)) {
                  tmp[id] = child;
                }
              }
              return tmp;
            })();
          }
        }

        if (childType) {
          childs = (() => {
            const tmp = {};
            for (let id in childs) {
              const child = childs[id];
              if (child.directChildType === childType) {
                tmp[id] = child;
              }
            }
            return tmp;
          })();
        }

        childs.length = Object.keys(childs).length;
        return childs;
        // const tmpData = this.getData();
        // const $update = this.update.bind(this);
        // let childs = [];
        // if (cls) {
        //   tmpData.forEach((item: any, ii: number) => {
        //     const itClassName = item.itemClass || '';
        //     if (itClassName.indexOf(cls) > -1) {
        //       childs.push({ index: ii, item });
        //     }
        //   });
        // } else {
        //   childs = tmpData.map((item: any, ii: number) => ({
        //     index: ii,
        //     item,
        //   }));
        // }
        // return {
        //   addClass(cls?: string, cb?: Function) {
        //     childs.forEach((child: any) => {
        //       const { index, item } = child;
        //       item.itemClass = item.itemClass
        //         ? item.itemClass + ' ' + cls
        //         : cls;
        //       tmpData[index] = item;
        //     });
        //     $update(tmpData, cb);
        //   },
        //   removeClass(cls?: string, cb?: Function) {
        //     childs.forEach((child: any) => {
        //       const { index, item } = child;
        //       if (item.itemClass && item.itemClass.indexOf(cls) > -1) {
        //         const re = new RegExp(`${cls} *`, 'g');
        //         item.itemClass = item.itemClass.replace(re, '').trim();
        //       }
        //       tmpData[index] = item;
        //     });
        //     $update(tmpData, cb);
        //   },
        //   css(sty: object, cb?: Function) {
        //     childs.forEach((child: any) => {
        //       const { index, item } = child;
        //       item.itemStyle = { ...(item.itemStyle || {}), ...sty };
        //       tmpData[index] = item;
        //     });
        //     $update(tmpData, cb);
        //   },
        //   show(cb?: Function) {
        //     childs.forEach((child: any) => {
        //       const { index, item } = child;
        //       const itStyle = item.itemStyle || {};
        //       if (itStyle.display === 'none') {
        //         delete itStyle.display;
        //       }
        //       item.itemStyle = itStyle;
        //       tmpData[index] = item;
        //     });
        //     $update(tmpData, cb);
        //   },
        //   hide(cb?: Function) {
        //     childs.forEach((child: any) => {
        //       const { index, item } = child;
        //       const itStyle = item.itemStyle || {};
        //       itStyle.display = 'none';
        //       item.itemStyle = itStyle;
        //       tmpData[index] = item;
        //     });
        //     $update(tmpData, cb);
        //   },
        // };
      },
      find(param: any) {
        const index = this.findIndex(param);
        return data[index];
      },
      findIndex(param) {
        // attr ???
        let tmpData = [...data];
        let index = -1;
        if (isNumber(param)) {
          index = param;
        }
        if (isString(param)) {
          param = { title: param };
        }
        if (isObject(param) && !React.isValidElement(param)) {
          let selected = -1;
          let hited = 0;
          for (let ii = 0; ii < tmpData.length; ii++) {
            const item = tmpData[ii];
            const keys = Object.keys(param);
            keys.forEach((ky, jj) => {
              const val = param[ky];
              hited = jj + 1;
              if (item[ky] === val) {
                selected = ii;
              } else {
                selected = -1;
              }
            });
            if (hited === keys.length && selected > -1) {
              index = selected;
              break;
            }
          }
        }
        if (isFunction(param)) {
          index = param(tmpData);
        }
        return index;
      },
      addClass(cls: string, cb?: Function) {
        cls = cls.replace('.', '');
        if (!this.hasClass(cls)) {
          const newClassName = className ? className + ' ' + cls : cls;
          propsRef.current.listClass = newClassName;
          setClassName(newClassName);
          actionCallback(cb);
        }
      },
      removeClass(cls: string, cb?: Function) {
        cls = cls.replace('.', '');
        if (this.hasClass(cls)) {
          const re = new RegExp(`${cls} *`, 'g');
          const tmpCls = propsRef.current.listClass.replace(re, '').trim();
          propsRef.current.listClass = tmpCls;
          setClassName(propsRef.current.listClass);
          actionCallback(cb);
        }
      },
      hasClass(cls: string) {
        cls = cls.replace('.', '');
        const { listClass } = propsRef.current;
        if (listClass && listClass.indexOf(cls) > -1) {
          return true;
        }
        return false;
      },
      toggleClass(cls: string, cb?: Function) {
        if (this.hasClass(cls)) {
          this.removeClass(cls, cb);
        } else {
          this.addClass(cls, cb);
        }
      },
      css(sty: object, cb?: Function) {
        const tmpStyle = { ...style, ...sty };
        propsRef.current.listStyle = tmpStyle;
        setStyle(tmpStyle);
        actionCallback(cb);
      },
      show() {
        const tmpStyle: any = { ...style };
        if (tmpStyle.display === 'none') {
          delete tmpStyle.display;
        }
        propsRef.current.listStyle = tmpStyle;
        setStyle(tmpStyle);
      },
      hide() {
        const tmpStyle: any = { ...style };
        tmpStyle.display = 'none';
        propsRef.current.listStyle = tmpStyle;
        setStyle(tmpStyle);
      },
      update(index: number | Array<any> = -1, param: any, cb?: Function) {
        if (isArray(index)) {
          param = index;
          index = -1;
        }

        if (isObject(param) && !React.isValidElement(param)) {
          if (index > -1) {
            const oriData = this.find(index);
            if (!isEmpty(oriData)) {
              const $data = { ...oriData, param };
              propsRef.current.data[index] = $data;
              setData({ ...propsRef.current.data });
              actionCallback(cb);
              return;
            }
          } else {
            if (isArray(param.data)) {
              param = param.data;
            }
          }
        }

        if (isArray(param)) {
          const tmpConfig = adapterProps(
            {
              ...propsRef.current,
              data: param,
              currentData: propsRef.current.data,
            },
            uniqId
          );
          propsRef.current.data = tmpConfig.data;
          setData(tmpConfig.data);
        }
        actionCallback(cb);
      },
      appendItem(pay: any, cb?: Function) {
        const index = data.length - 1;
        const itemData = adapterChildProps({
          index,
          subItem: pay,
          config: oriPropsRef.current,
          listUniqId: uniqId,
        });
        this.update([...data, itemData], cb);
      },
      prependItem(pay: any, cb?: Function) {
        const tmpData = [pay, ...propsRef.current.data];
        const tmpConfig = adapterProps(
          { ...propsRef.current, data: tmpData },
          uniqId
        );
        this.update(tmpConfig.data, cb);
      },
      removeItem(param: number | object | Function, cb?: Function) {
        let tmpData = [...data];
        let index = this.findIndex(param);
        if (index > -1) {
          tmpData.splice(index, 1);
          this.update(tmpData);
        }
        actionCallback(cb);
      },
      insertItem(param: number | object | Function, pay, any, cb?: Function) {
        let tmpData = [...data];
        let index = this.findIndex(param);
        if (index > -1) {
          tmpData.splice(index, 0, pay);
          const tmpConfig = adapterProps(
            { ...propsRef.current, data: tmpData },
            uniqId
          );
          this.update(tmpConfig.data);
        }
        actionCallback(cb);
      },
    };

    if (isFunction(lifeTimes.created)) {
      if (createdRef.current === false) {
        createdRef.current = true;
        lifeTimes.created.call(context);
      }
    }

    React.useEffect(() => {
      if (isFunction(lifeTimes.attached)) {
        lifeTimes.attached.call(context);
      }
    });

    React.useEffect(() => {
      if (uniqId && !globalElements[uniqId]) {
        globalElements[uniqId] = context;
      }
      if (id && !globalElements[id]) {
        globalElements[id] = context;
      }
      if (isFunction(lifeTimes.ready)) {
        lifeTimes.ready.call(context);
      }
      return () => {
        globalElements[context.uniqId] = null;
        globalElements[id] = null;
        delete globalElements[context.uniqId];
        delete globalElements[id];
        if (isFunction(lifeTimes.detached)) {
          lifeTimes.detached();
        }
      };
    }, []);

    const listHeader = React.useMemo(() => {
      const header = props.header;
      if (header) {
        if (isString(header) || isNumber(header)) {
          return <span className="list-header">{header}</span>;
        }
        if (React.isValidElement(header)) {
          return header;
        }
        if (isObject(header)) {
          const clsName =
            'list-header' + header.itemClass ? header.itemClass : '';
          return <Item {...header} itemClass={clsName} />;
        }
      }
    }, [header]);

    const listFooter = React.useMemo(() => {
      const footer = props.footer;
      if (footer) {
        if (isString(footer) || isNumber(footer)) {
          return <span className="list-footer">{footer}</span>;
        }
        if (React.isValidElement(footer)) {
          return footer;
        }
        if (isObject(footer)) {
          const clsName =
            'list-footer' + footer.itemClass ? footer.itemClass : '';
          return <Item {...footer} itemClass={clsName} />;
        }
      }
    }, [footer]);

    const listRender = React.useMemo(() => {
      return data.map((item: any, ii: number) => {
        return <Item {...item} />;
      });
    }, [data.length, data]);

    return (
      <div
        className={className}
        style={{ ...style }}
        id={props.id}
        // data-uniq-id={uniqId}
        {...events}
        {...attr}
      >
        {listHeader}
        {listRender}
        {listFooter}
      </div>
    );
  }
);
